/**
 * Audit logging service.
 *
 * Logs auth events (login, logout, 2FA, session) to the `audit_logs` table
 * via Supabase.  Falls back to `console.warn` so callers never throw.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type AuditAction =
  | "login_success"
  | "login_failure"
  | "logout"
  | "session_created"
  | "session_expired"
  | "2fa_enabled"
  | "2fa_disabled"
  | "2fa_verify_success"
  | "2fa_verify_failure"
  | "2fa_sms_sent"
  | "2fa_recovery_success"
  | "2fa_recovery_failure"
  | "2fa_recovery_codes_generated"
  | "consent_granted"
  | "consent_revoked"
  | "otp_sent"
  | "otp_send_failure"
  | "otp_verify_success"
  | "otp_verify_failure"
  | "jwt_tamper_detected"
  | "rate_limit_exceeded"
  | "logout_all_devices"
  | "login_blocked_banned"
  | "login_blocked_venue_suspended"
  | "third_party_transfer";

/** Origin of an audit write; stored in `audit_logs.metadata.source` (free-form string, e.g. app id or API path). */
export type AuditSource = string;

export interface AuditEntry {
  actor_id: string | null;
  action: AuditAction;
  ip_hash: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Mask an IP for PDPA audit trail.
 * IPv4: replace last octet with "xxx" (e.g. 203.0.113.xxx).
 * IPv6: replace last group with "xxxx".
 * Matches the convention used by edge functions (sign-dpa, record-consent,
 * validate-qr) and DB RPCs (record_user_consent, sign_dpa) so audit_logs.ip_hash
 * has a single consistent format.
 */
function maskIp(ip: string): string {
  if (ip.includes(":")) {
    const parts = ip.split(":");
    parts[parts.length - 1] = "xxxx";
    return parts.join(":");
  }
  const parts = ip.split(".");
  parts[parts.length - 1] = "xxx";
  return parts.join(".");
}

/** Best-effort masked IP from public API (returns null if unavailable). */
let cachedIpMasked: string | null = null;

async function getIpMasked(): Promise<string | null> {
  if (cachedIpMasked) return cachedIpMasked;
  try {
    const res = await fetch("https://api.ipify.org?format=text", {
      signal: AbortSignal.timeout(2000),
    });
    if (res.ok) {
      const ip = (await res.text()).trim();
      if (!ip) return null;
      cachedIpMasked = maskIp(ip);
      return cachedIpMasked;
    }
  } catch {
    // Fail silently
  }
  return null;
}

function getUserAgent(): string | null {
  try {
    return navigator.userAgent ?? null;
  } catch {
    return null;
  }
}

// ─── SupabaseClient interface (avoid importing the full lib) ────────────────
interface MinimalSupabase {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rpc: (fn: string, args?: Record<string, unknown>) => PromiseLike<{ error: any }>;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Log an audit event.
 *
 * @param supabase - The Supabase client (passed to avoid circular deps)
 * @param action   - What happened
 * @param actorId  - Authenticated user ID (null for anonymous events)
 * @param metadata - Arbitrary JSON metadata
 * @param source     - Origin of this action (`metadata.source`, e.g. page path or API route)
 */
export async function logAuditEvent(
  supabase: MinimalSupabase,
  action: AuditAction,
  actorId: string | null = null,
  metadata: Record<string, unknown> = {},
  entityType: string = "auth",
  source?: AuditSource,
): Promise<void> {
  try {
    const [ipMasked, userAgent] = await Promise.all([
      getIpMasked(),
      Promise.resolve(getUserAgent()),
    ]);

    const entry: AuditEntry = {
      actor_id: actorId,
      action,
      ip_hash: ipMasked,
      user_agent: userAgent,
      metadata,
    };

    const { error } = await supabase.rpc("log_audit", {
      p_action: entry.action,
      p_entity_type: entityType,
      p_ip_hash: entry.ip_hash,
      p_metadata: {
        ...entry.metadata,
        user_agent: entry.user_agent,
        ...(source !== undefined ? { source } : {}),
      },
    });

    if (error) {
      console.warn("[audit] Failed to log event:", action, error);
    }
  } catch (err) {
    console.warn("[audit] Failed to log event:", action, err);
  }
}

/**
 * Log an onward transfer of personal data to a data intermediary (e.g. SMS
 * gateway, email dispatch provider). Required by PDPA Section 4 / docs/PDPA.md
 * §3.14 — every disclosure to an intermediary must be evidenceable in the audit
 * trail.
 */
export async function logThirdPartyTransfer(
  supabase: MinimalSupabase,
  params: {
    intermediary: string;
    purpose: string;
    actorId?: string | null;
    metadata?: Record<string, unknown>;
    source?: AuditSource;
  },
): Promise<void> {
  const { intermediary, purpose, actorId = null, metadata = {}, source } = params;
  await logAuditEvent(
    supabase,
    "third_party_transfer",
    actorId,
    { intermediary, purpose, ...metadata },
    "data_intermediary",
    source,
  );
}
